import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { LoadPanel, SelectBox, Popup } from "devextreme-react";
import DataGrid, {
  Column,
  ColumnChooser,
  FilterRow,
  Grouping,
  GroupPanel,
  HeaderFilter,
  Pager,
  Paging,
  SearchPanel,
  Selection,
  Summary,
  TotalItem,
  Toolbar,
  Item,
} from "devextreme-react/data-grid";
import { useNavigate } from "react-router-dom";

import { DynamicTabb } from "../../../components/DynamicTab/DynamicTab";
import { Toster } from "../../../components/Toster/Toster";
import { handleGetLocal } from "../../../services/auth-service";
import { logOut } from "../../../components/Logout";

import "./RevenueReport.css";
import query from "devextreme/data/query";

const pageSizes = [25, 50, 100];

const months = [
  { id: 1, name: "January" },
  { id: 2, name: "February" },
  { id: 3, name: "March" },
  { id: 4, name: "April" },
  { id: 5, name: "May" },
  { id: 6, name: "June" },
  { id: 7, name: "July" },
  { id: 8, name: "August" },
  { id: 9, name: "September" },
  { id: 10, name: "October" },
  { id: 11, name: "November" },
  { id: 12, name: "December" },
];

const getMonthRange = (type) => {
  const today = new Date();

  const currentMonth = today.getMonth() + 1;
  const currentYear = today.getFullYear();

  switch (type) {
    case "This Month":
      return {
        startingMonth: currentMonth,
        startingYear: currentYear,
        endingMonth: currentMonth,
        endingYear: currentYear,
      };

    case "Last Month": {
      const lastMonthDate = new Date(currentYear, currentMonth - 2, 1);

      return {
        startingMonth: lastMonthDate.getMonth() + 1,
        startingYear: lastMonthDate.getFullYear(),
        endingMonth: lastMonthDate.getMonth() + 1,
        endingYear: lastMonthDate.getFullYear(),
      };
    }

    case "This Quarter": {
      const quarterStartMonth = Math.floor((currentMonth - 1) / 3) * 3 + 1;

      return {
        startingMonth: quarterStartMonth,
        startingYear: currentYear,
        endingMonth: quarterStartMonth + 2,
        endingYear: currentYear,
      };
    }

    case "Last Quarter": {
      const currentQuarterStartMonth = Math.floor((currentMonth - 1) / 3) * 3 + 1;

      let endMonth = currentQuarterStartMonth - 1;
      let endYear = currentYear;

      if (endMonth <= 0) {
        endMonth += 12;
        endYear -= 1;
      }

      let startMonth = endMonth - 2;
      let startYear = endYear;

      if (startMonth <= 0) {
        startMonth += 12;
        startYear -= 1;
      }

      return {
        startingMonth: startMonth,
        startingYear: startYear,
        endingMonth: endMonth,
        endingYear: endYear,
      };
    }

    case "This Year":
      return {
        startingMonth: 1,
        startingYear: currentYear,
        endingMonth: 12,
        endingYear: currentYear,
      };

    case "Last Year":
      return {
        startingMonth: 1,
        startingYear: currentYear - 1,
        endingMonth: 12,
        endingYear: currentYear - 1,
      };

    default:
      return {
        startingMonth: currentMonth,
        startingYear: currentYear,
        endingMonth: currentMonth,
        endingYear: currentYear,
      };
  }
};

const RevenueReport = () => {
  const navigate = useNavigate();
  const gridRef = useRef(null);
  const detailGridRef = useRef(null);

  const pathname = handleGetLocal("pathURL");

  const today = new Date();
  const currentMonth = today.getMonth() + 1;
  const currentYear = today.getFullYear();

  const [startingMonth, setStartingMonth] = useState(currentMonth);
  const [startingYear, setStartingYear] = useState(currentYear);

  const [endingMonth, setEndingMonth] = useState(currentMonth);
  const [endingYear, setEndingYear] = useState(currentYear);

  const [quickDate, setQuickDate] = useState("This Month");

  const [revenueData, setRevenueData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailPopupOpen, setDetailPopupOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState([]);
  const [selectedRevenueRow, setSelectedRevenueRow] = useState(null);
  const [filteredKpis, setFilteredKpis] = useState({
    groupedResults: 0,
    totalBillableDays: 0,
    totalRevenue: 0,
  });

  const [toastConfig, setToastConfig] = useState({
    isVisible: false,
    type: "",
    message: "",
  });

  const quickDateChangingRef = useRef(false);

  const yearOptions = useMemo(() => {
    const years = [];

    for (let year = currentYear + 1; year >= currentYear - 10; year--) {
      years.push(year);
    }

    return years;
  }, [currentYear]);

  const quickDateOptions = ["This Month", "Last Month", "This Quarter", "Last Quarter", "This Year", "Last Year"];

  const showToast = (message, type = "error") => {
    setToastConfig({
      isVisible: true,
      type,
      message,
    });

    setTimeout(() => {
      setToastConfig({
        isVisible: false,
        type: "",
        message: "",
      });
    }, 2500);
  };

  const updateFilteredKpis = () => {
    const grid = gridRef.current?.instance();

    if (!grid) return;

    try {
      const combinedFilter = grid.getCombinedFilter(true);

      const filteredRows = combinedFilter ? query(revenueData).filter(combinedFilter).toArray() : revenueData;

      setFilteredKpis({
        groupedResults: filteredRows.length,

        totalBillableDays: filteredRows.reduce((total, row) => total + Number(row.billableDays || 0), 0),

        totalRevenue: filteredRows.reduce((total, row) => total + Number(row.revenue || 0), 0),
      });
    } catch (error) {
      console.error("Unable to calculate filtered KPIs:", error);
    }
  };

  useEffect(() => {
    const grid = gridRef.current?.instance();

    if (!grid) {
      setFilteredKpis({
        groupedResults: revenueData.length,
        totalBillableDays: revenueData.reduce((total, row) => total + Number(row.billableDays || 0), 0),
        totalRevenue: revenueData.reduce((total, row) => total + Number(row.revenue || 0), 0),
      });

      return;
    }

    const combinedFilter = grid.getCombinedFilter(true);

    const filteredRows = combinedFilter ? query(revenueData).filter(combinedFilter).toArray() : revenueData;

    setFilteredKpis({
      groupedResults: filteredRows.length,

      totalBillableDays: filteredRows.reduce((total, row) => total + Number(row.billableDays || 0), 0),

      totalRevenue: filteredRows.reduce((total, row) => total + Number(row.revenue || 0), 0),
    });
  }, [revenueData]);

  const getRevenueReport = () => {
    const startPeriod = new Date(startingYear, startingMonth - 1, 1);

    const endPeriod = new Date(endingYear, endingMonth - 1, 1);

    if (startPeriod > endPeriod) {
      showToast("The starting period cannot be after the ending period.");

      return;
    }

    setLoading(true);

    axios
      .get(`${process.env.REACT_APP_API_URL}/api/report-center/revenue`, {
        params: {
          startingMonth,
          startingYear,
          endingMonth,
          endingYear,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          Domain: `${pathname}`,
        },
      })
      .then((res) => {
        setLoading(false);

        const rows = Array.isArray(res?.data) ? res.data : [];

        setRevenueData(
          rows.map((row, index) => ({
            ...row,
            rowId: `${row?.periodStart || ""}-${
              row?.sentenceCourtEntityId || ""
            }-${row?.violationDescription || ""}-${index}`,
          })),
        );
      })
      .catch((err) => {
        setLoading(false);

        console.error("Revenue Report API Error:", err);

        if (!err?.response) {
          navigate("/error/500");
        } else if (err?.response?.status === 401) {
          logOut();
        } else {
          const message =
            typeof err?.response?.data === "string" ? err.response.data : "Unable to load Revenue Report.";

          showToast(message);
        }
      });
  };

  const handleQuickDateChange = (value) => {
    if (!value) {
      setQuickDate(null);
      return;
    }

    quickDateChangingRef.current = true;

    setQuickDate(value);

    const range = getMonthRange(value);

    setStartingMonth(range.startingMonth);
    setStartingYear(range.startingYear);
    setEndingMonth(range.endingMonth);
    setEndingYear(range.endingYear);

    setTimeout(() => {
      quickDateChangingRef.current = false;
    }, 0);
  };

  const clearGridFilters = () => {
    const grid = gridRef.current?.instance();

    if (!grid) return;

    grid.clearFilter();
    grid.clearGrouping();
    grid.clearSelection();

    setFilteredKpis({
      groupedResults: revenueData.length,

      totalBillableDays: revenueData.reduce((total, row) => total + Number(row.billableDays || 0), 0),

      totalRevenue: revenueData.reduce((total, row) => total + Number(row.revenue || 0), 0),
    });
  };

  const exportCsv = () => {
    if (!revenueData.length) {
      showToast("There is no Revenue Report data to export.");

      return;
    }

    const headers = ["Period", "Company", "Region", "Court", "Equipment", "Average Rate", "Billable Days", "Revenue"];

    const escapeCsvValue = (value) => {
      if (value === null || value === undefined) {
        return "";
      }

      const stringValue = String(value);

      if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }

      return stringValue;
    };

    const rows = revenueData.map((row) => [
      row.period,
      row.company,
      row.regionName,
      row.courtName,
      row.violationDescription,
      Number(row.averageRate || 0).toFixed(2),
      row.billableDays,
      Number(row.revenue || 0).toFixed(2),
    ]);

    const csv = [headers.map(escapeCsvValue).join(","), ...rows.map((row) => row.map(escapeCsvValue).join(","))].join(
      "\n",
    );

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    const anchor = document.createElement("a");

    anchor.href = url;
    anchor.download = `Revenue_Report_${startingYear}_${startingMonth}_to_${endingYear}_${endingMonth}.csv`;

    document.body.appendChild(anchor);

    anchor.click();

    document.body.removeChild(anchor);

    URL.revokeObjectURL(url);
  };

  const printReport = () => {
    window.print();
  };

  const groupedResults = revenueData.length;

  const totalBillableDays = useMemo(() => {
    return revenueData.reduce((total, row) => total + Number(row.billableDays || 0), 0);
  }, [revenueData]);

  const totalRevenue = useMemo(() => {
    return revenueData.reduce((total, row) => total + Number(row.revenue || 0), 0);
  }, [revenueData]);

  useEffect(() => {
    getRevenueReport();
  }, [startingMonth, startingYear, endingMonth, endingYear]);

  const getRevenueReportDetail = (summaryRow) => {
    if (!summaryRow) {
      return;
    }

    if (!summaryRow.periodStart) {
      showToast("Period Start was not found for the selected row.");
      return;
    }

    if (!summaryRow.sentenceCourtEntityId) {
      showToast("Court Entity ID was not found for the selected row.");
      return;
    }
    const agencyPay = summaryRow.isAgencyBilling === true;
    const violationDescription = agencyPay
      ? (summaryRow.violationDescription || "").replace(/^Invoice\s+/i, "")
      : summaryRow.violationDescription || "";

    setDetailLoading(true);
    setSelectedRevenueRow(summaryRow);
    setDetailData([]);
    setDetailPopupOpen(true);
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/report-center/revenue/detail`, {
        params: {
          periodStart: summaryRow.periodStart,
          sentenceCourtEntityId: summaryRow.sentenceCourtEntityId,
          violationDescription: violationDescription,
          AgencyPay: agencyPay,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          Domain: `${pathname}`,
        },
      })
      .then((res) => {
        setDetailLoading(false);
        const rows = Array.isArray(res?.data) ? res.data : [];
        setDetailData(
          rows.map((row, index) => ({
            ...row,
            detailRowId: row?.periodObligationId || row?.obligationId || `revenue-detail-${index}`,
          })),
        );
      })
      .catch((err) => {
        setDetailLoading(false);
        if (!err?.response) {
          setDetailPopupOpen(false);
          navigate("/error/500");
        } else if (err?.response?.status === 401) {
          setDetailPopupOpen(false);
          logOut();
        } else {
          const message =
            typeof err?.response?.data === "string" ? err.response.data : "Unable to load Revenue Report details.";

          showToast(message);
        }
      });
  };

  const handleRevenueRowClick = (e) => {
    if (e?.rowType !== "data" || !e?.data) {
      return;
    }

    getRevenueReportDetail(e.data);
  };
  const clearDetailFilters = () => {
    const detailGrid = detailGridRef.current?.instance();

    if (!detailGrid) {
      return;
    }

    detailGrid.clearFilter();
    detailGrid.clearSelection();
  };

  const detailPopupTitle = useMemo(() => {
    if (!selectedRevenueRow) {
      return "Revenue Detail";
    }

    const court = selectedRevenueRow.courtName || "Revenue Detail";

    const period = selectedRevenueRow.period || "";

    return period ? `${court} — ${period}` : court;
  }, [selectedRevenueRow]);

  const detailPopupSubtitle = useMemo(() => {
    if (!selectedRevenueRow) {
      return "";
    }

    const company = selectedRevenueRow.company || "—";

    const region = selectedRevenueRow.regionName || "—";

    const equipment = selectedRevenueRow.violationDescription || "—";

    return `${company} · ${region} · ${equipment}`;
  }, [selectedRevenueRow]);
  return (
    <>
      <LoadPanel shadingColor="rgba(0, 0, 0, 0.15)" visible={loading} delay={10} />
      <Toster message={toastConfig.message} type={toastConfig.type} visible={toastConfig.isVisible} />
      <Popup
        visible={detailPopupOpen}
        onHiding={() => {
          setDetailPopupOpen(false);
          setSelectedRevenueRow(null);
          setDetailData([]);
        }}
        showTitle={false}
        showCloseButton={false}
        hideOnOutsideClick={false}
        dragEnabled={false}
        width="80%"
        maxWidth={1200}
        height="88%"
        maxHeight={900}
        wrapperAttr={{
          class: "revenue-detail-popup-wrapper",
        }}
      >
        <div className="revenue-detail-popup">
          <LoadPanel
            shadingColor="rgba(0, 0, 0, 0.08)"
            visible={detailLoading}
            delay={10}
            container=".revenue-detail-popup"
          />
          <div className="revenue-detail-header">
            <h2>{detailPopupTitle}</h2>
            <button
              type="button"
              className="revenue-detail-close"
              onClick={() => {
                setDetailPopupOpen(false);
                setSelectedRevenueRow(null);
                setDetailData([]);
              }}
              aria-label="Close"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <div className="revenue-detail-subtitle">{detailPopupSubtitle}</div>
          <div className="revenue-detail-grid-container">
            <DataGrid
              ref={detailGridRef}
              dataSource={detailData}
              keyExpr="detailRowId"
              showBorders={true}
              showRowLines={true}
              showColumnLines={false}
              rowAlternationEnabled={true}
              allowColumnReordering={true}
              allowColumnResizing={true}
              columnResizingMode="widget"
              columnAutoWidth={true}
              hoverStateEnabled={true}
              wordWrapEnabled={false}
              width="100%"
              height="100%"
              noDataText="No revenue detail records found."
            >
              <Toolbar>
                <Item location="before">
                  <div />
                </Item>
                <Item name="searchPanel" location="after" />
                <Item location="after">
                  <button type="button" className="revenue-detail-clear-button" onClick={clearDetailFilters}>
                    <span className="revenue-clear-icon">×</span>
                    Clear filters
                  </button>
                </Item>
              </Toolbar>

              <SearchPanel visible={true} width={220} placeholder="Search detail..." highlightSearchText={true} />
              <HeaderFilter
                visible={true}
                search={{
                  enabled: true,
                }}
              />
              <FilterRow visible={false} />
              <Selection mode="single" />
              <Column dataField="lastFirst" caption="Offender" minWidth={190} />
              <Column dataField="offenderNumber" caption="Offender #" minWidth={90} />
              <Column dataField="sourceType" caption="Source" minWidth={90} />
              <Column dataField="reference" caption="Reference" minWidth={130} />
              <Column dataField="billableStart" caption="Start" dataType="date" format="MMM d, yyyy" minWidth={135} />
              <Column dataField="billableEnd" caption="End" dataType="date" format="MMM d, yyyy" minWidth={135} />

              <Column
                dataField="billableDays"
                caption="Days"
                dataType="number"
                alignment="left"
                width={90}
                format="#,##0"
              />
              <Column
                dataField="rate"
                caption="Rate"
                dataType="number"
                alignment="left"
                width={115}
                format={{
                  type: "currency",
                  precision: 2,
                }}
              />
              <Column
                dataField="revenue"
                caption="Revenue"
                dataType="number"
                alignment="left"
                width={170}
                format={{
                  type: "currency",
                  precision: 2,
                }}
              />
              <Column dataField="company" caption="Company" visible={false} />
              <Column dataField="regionName" caption="Region" visible={false} />
              <Column dataField="courtName" caption="Court" visible={false} />
              <Column dataField="violationDescription" caption="Equipment" visible={false} />
              <Column dataField="rateStart" caption="Rate Start" dataType="date" visible={false} format="MMM d, yyyy" />
              <Column dataField="rateEnd" caption="Rate End" dataType="date" visible={false} format="MMM d, yyyy" />
              <Column dataField="periodObligationId" visible={false} showInColumnChooser={false} />
              <Column dataField="obligationId" visible={false} showInColumnChooser={false} />

              <Summary>
                <TotalItem column="billableDays" summaryType="sum" valueFormat="#,##0" displayFormat="{0}" />

                <TotalItem
                  column="revenue"
                  summaryType="sum"
                  valueFormat={{
                    type: "currency",
                    precision: 2,
                  }}
                  displayFormat="Sum: {0}"
                />
              </Summary>

              <Paging defaultPageSize={25} />

              <Pager
                visible={true}
                allowedPageSizes={[25, 50, 100]}
                showPageSizeSelector={true}
                showNavigationButtons={true}
                showInfo={true}
                infoText="Page {0} of {1} ({2} items)"
              />
            </DataGrid>
          </div>
        </div>
      </Popup>

      <div className="page-section revenue-report-page">
        <DynamicTabb />

        <div className="page-content revenue-modern-content">
          <div className="screen_num">
            <p>FN.15</p>
          </div>
          <div className="revenue-report-period-card">
            <div className="revenue-section-heading">
              <div className="revenue-section-icon">
                <svg width="21" height="21" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M19 4H18V2H16V4H8V2H6V4H5C3.9 4 3 4.9 3 6V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V9H19V20ZM7 11H12V16H7V11Z"
                    fill="currentColor"
                  />
                </svg>
              </div>

              <div>
                <h2>Report Period</h2>
                <p>Choose the starting and ending month for the revenue calculation.</p>
              </div>
            </div>

            <div className="revenue-report-period-row">
              <div className="revenue-period-section">
                <label>Starting Period</label>

                <div className="revenue-period-fields">
                  <SelectBox
                    dataSource={months}
                    valueExpr="id"
                    displayExpr="name"
                    value={startingMonth}
                    onValueChanged={(e) => {
                      setStartingMonth(e.value);

                      if (!quickDateChangingRef.current) {
                        setQuickDate(null);
                      }
                    }}
                  />

                  <SelectBox
                    dataSource={yearOptions}
                    value={startingYear}
                    onValueChanged={(e) => {
                      setStartingYear(e.value);

                      if (!quickDateChangingRef.current) {
                        setQuickDate(null);
                      }
                    }}
                  />
                </div>
              </div>
              <div className="revenue-period-section">
                <label>Ending Period</label>

                <div className="revenue-period-fields">
                  <SelectBox
                    dataSource={months}
                    valueExpr="id"
                    displayExpr="name"
                    value={endingMonth}
                    onValueChanged={(e) => {
                      setEndingMonth(e.value);

                      if (!quickDateChangingRef.current) {
                        setQuickDate(null);
                      }
                    }}
                  />

                  <SelectBox
                    dataSource={yearOptions}
                    value={endingYear}
                    onValueChanged={(e) => {
                      setEndingYear(e.value);

                      if (!quickDateChangingRef.current) {
                        setQuickDate(null);
                      }
                    }}
                  />
                </div>
              </div>
              <div className="revenue-quick-date">
                <label>Quick Date</label>

                <SelectBox
                  dataSource={quickDateOptions}
                  value={quickDate}
                  width="100%"
                  placeholder="Select"
                  showClearButton={true}
                  onValueChanged={(e) => handleQuickDateChange(e.value)}
                />
              </div>
              <div className="revenue-refresh-container">
                <button className="revenue-primary-button" type="button" onClick={getRevenueReport}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M17.65 6.35C16.02 4.72 13.71 3.78 11.17 4.04C7.5 4.41 4.48 7.39 4.07 11.06C3.52 15.91 7.27 20 12 20C15.19 20 17.93 18.13 19.21 15.44C19.53 14.77 19.05 14 18.31 14C17.94 14 17.59 14.2 17.43 14.53C16.3 16.96 13.59 18.5 10.63 17.84C8.41 17.35 6.62 15.54 6.15 13.32C5.31 9.44 8.26 6 12 6C13.66 6 15.14 6.69 16.22 7.78L14.71 9.29C14.08 9.92 14.52 11 15.41 11H19C19.55 11 20 10.55 20 10V6.41C20 5.52 18.92 5.07 18.29 5.7L17.65 6.35Z"
                      fill="currentColor"
                    />
                  </svg>
                  Refresh
                </button>
              </div>
            </div>
          </div>
          <div className="revenue-kpi-container">
            <div className="revenue-kpi-card revenue-kpi-results">
              <div className="revenue-kpi-icon">
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <path d="M4 21V10H8V21H4ZM10 21V3H14V21H10ZM16 21V13H20V21H16Z" fill="currentColor" />
                </svg>
              </div>

              <div>
                <span>GROUPED RESULTS</span>
                <strong>{filteredKpis.groupedResults.toLocaleString()}</strong>
              </div>
            </div>

            <div className="revenue-kpi-card revenue-kpi-days">
              <div className="revenue-kpi-icon">
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <path
                    d="M19 4H18V2H16V4H8V2H6V4H5C3.9 4 3 4.9 3 6V20C3 21.1 3.9 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V9H19V20Z"
                    fill="currentColor"
                  />
                </svg>
              </div>

              <div>
                <span>BILLABLE DAYS</span>
                <strong>{filteredKpis.totalBillableDays.toLocaleString()}</strong>
              </div>
            </div>

            <div className="revenue-kpi-card revenue-total-card">
              <div className="revenue-kpi-icon revenue-money-icon">$</div>

              <div>
                <span>TOTAL REVENUE</span>

                <strong>
                  {filteredKpis.totalRevenue.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
                </strong>
              </div>
            </div>
          </div>
          <div className="revenue-grid-card">
            <div className="revenue-grid-title">
              <div className="revenue-grid-heading">
                <div className="revenue-section-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24">
                    <path
                      d="M3 3H21V21H3V3ZM5 5V8H19V5H5ZM5 10V19H9V10H5ZM11 10V19H15V10H11ZM17 10V19H19V10H17Z"
                      fill="currentColor"
                    />
                  </svg>
                </div>

                <div>
                  <h2>Revenue Summary</h2>

                  <p>Drag a column header here to group. Use filters or search to find specific records.</p>
                </div>
              </div>

              <div className="revenue-grid-buttons">
                <button type="button" className="revenue-secondary-button" onClick={clearGridFilters}>
                  <span className="revenue-clear-icon">×</span>
                  Clear Filters
                </button>

                <button type="button" className="revenue-secondary-button" onClick={exportCsv}>
                  <svg width="17" height="17" viewBox="0 0 24 24">
                    <path
                      d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2ZM14 4L18 8H14V4ZM11 11H13V15H16L12 19L8 15H11V11Z"
                      fill="currentColor"
                    />
                  </svg>
                  Export CSV
                </button>

                <button type="button" className="revenue-secondary-button" onClick={printReport}>
                  <svg width="17" height="17" viewBox="0 0 24 24">
                    <path
                      d="M19 8H5C3.34 8 2 9.34 2 11V16H6V21H18V16H22V11C22 9.34 20.66 8 19 8ZM16 19H8V14H16V19ZM18 12C17.45 12 17 11.55 17 11C17 10.45 17.45 10 18 10C18.55 10 19 10.45 19 11C19 11.55 18.55 12 18 12ZM18 3H6V7H18V3Z"
                      fill="currentColor"
                    />
                  </svg>
                  Print
                </button>
              </div>
            </div>

            <DataGrid
              ref={gridRef}
              dataSource={revenueData}
              onRowClick={handleRevenueRowClick}
              onOptionChanged={(e) => {
                if (
                  e.fullName?.includes("filterValue") ||
                  e.fullName?.includes("filterValues") ||
                  e.fullName === "searchPanel.text" ||
                  e.fullName?.includes("groupIndex")
                ) {
                  setTimeout(() => {
                    updateFilteredKpis();
                  }, 0);
                }
              }}
              keyExpr="rowId"
              showBorders={false}
              showRowLines={true}
              showColumnLines={false}
              rowAlternationEnabled={false}
              allowColumnReordering={true}
              allowColumnResizing={true}
              columnResizingMode="widget"
              columnAutoWidth={true}
              hoverStateEnabled={true}
              wordWrapEnabled={false}
              width="100%"
              height={600}
              noDataText="No revenue data found for the selected period."
            >
              <Toolbar>
                <Item name="groupPanel" location="before" />
                <Item name="searchPanel" location="after" />
                <Item name="columnChooserButton" location="after" />
              </Toolbar>

              <GroupPanel visible={true} emptyPanelText="Drag a column header here to group" />

              <Grouping autoExpandAll={false} contextMenuEnabled={true} />

              <SearchPanel visible={true} width={245} placeholder="Search all columns..." highlightSearchText={true} />

              <FilterRow visible={false} />
              <HeaderFilter
                visible={true}
                search={{
                  enabled: true,
                }}
              />

              {/* <ColumnChooser enabled={true} /> */}
              <ColumnChooser height="340px" width="400px" enabled={true} mode="dragAndDrop" />

              <Selection mode="single" />

              <Column dataField="period" caption="Period" minWidth={105} />

              <Column dataField="company" caption="Company" minWidth={180} />

              <Column dataField="regionName" caption="Region" minWidth={170} />

              <Column dataField="courtName" caption="Court" minWidth={220} />
              <Column dataField="isAgencyBilling" caption="Agency Billing" dataType="boolean" minWidth={140} />

              <Column dataField="violationDescription" caption="Equipment" minWidth={220} />

              <Column
                dataField="averageRate"
                caption="Avg Rate"
                dataType="number"
                alignment="left"
                width={125}
                format={{
                  type: "currency",
                  precision: 2,
                }}
              />

              <Column
                dataField="billableDays"
                caption="Days"
                dataType="number"
                alignment="left"
                width={100}
                format="#,##0"
              />

              <Column
                dataField="revenue"
                caption="Revenue"
                dataType="number"
                alignment="left"
                minWidth={140}
                format={{
                  type: "currency",
                  precision: 2,
                }}
              />

              <Column dataField="sentenceCourtEntityId" visible={false} showInColumnChooser={false} />

              <Column dataField="periodStart" visible={false} dataType="date" />

              <Summary>
                <TotalItem column="violationDescription" summaryType="count" displayFormat="{0} visible rows" />

                <TotalItem column="billableDays" summaryType="sum" valueFormat="#,##0" displayFormat="{0}" />

                <TotalItem
                  column="revenue"
                  summaryType="sum"
                  valueFormat={{
                    type: "currency",
                    precision: 2,
                  }}
                  displayFormat="Sum: {0}"
                />
              </Summary>

              <Paging defaultPageSize={25} />

              <Pager
                visible={true}
                allowedPageSizes={pageSizes}
                showPageSizeSelector={true}
                showNavigationButtons={true}
                showInfo={true}
                infoText="Page {0} of {1} ({2} items)"
              />
            </DataGrid>
          </div>
        </div>
      </div>
    </>
  );
};

export default RevenueReport;
